import type {QueryClient} from '@tanstack/react-query'
import isEqual from 'lodash-es/isEqual'

import type {MemexProjectColumnId} from '../../../../api/columns/contracts/memex-column'
import type {AgentTask, MemexItem, UpdateMemexItemResponseData} from '../../../../api/memex-items/contracts'
import {createMemexItemModel, type MemexItemModel} from '../../../../models/memex-item-model'
import {useSidePanelItemQuery} from '../../../../queries/side-panel'
import {mergeItemData} from '../../memex-item-helpers'
import {isPageParamsDataGrouped, isQueryPageOfItems} from '../../queries/query-data-helpers'
import {
  buildPaginatedTotalCountsQueryKey,
  createGroupedItemsId,
  getPageTypeFromQueryKey,
  type GroupId,
  isPageTypeForGroupedItems,
  isQueryKeyForGroupedItems,
  isQueryKeyForGroupedItemsPageType,
  isQueryKeyForSubIssues,
  type PaginatedMemexItemsQueryKey,
} from '../../queries/query-keys'
import {
  type GroupedPageParamsQueryData,
  type MemexItemsPageQueryData,
  type MemexItemsTotalCountsQueryData,
  type MemexPageQueryData,
  pageParamForInitialPage,
} from '../../queries/types'
import type {ReorderItemData} from '../../types'
import {
  getQueryDataForMemexItemsTotalCounts,
  incrementMemexItemsTotalCountForGroup,
  mergeQueryDataForMemexItemsTotalCounts,
} from '../memex-totals'
import {getPageParamsQueryDataForVariables, setPageParamsQueryDataForVariables} from '../page-params'
import {getQueryDataForSidePanelItemFromQueryClient} from './get-query-data'
import {
  getActiveMemexItemsQueryVariables,
  getQueryKeyForFirstGroupedItemsPage,
  getQueryKeysForItem,
} from './item-helpers'
import {findMemexItemByIdInQueryClient} from './read-items'

export type OptimisticUpdateRollbackData = {
  queryData: Array<{queryKey: PaginatedMemexItemsQueryKey; queryData: MemexItemsPageQueryData}>
  totalCounts: MemexItemsTotalCountsQueryData
}

/**
 * Updates the local state of a MemexItemModel with new values from the server
 * if the item is visible in the side panel.
 * A new MemexItemModel is created to replace the existing item in our client-side state,
 * so that a re-render is triggered.
 * @param queryClient
 * @param newMemexItem New data that we're looking to merge values into an existingMemexItemModel in our client state
 */
function updateSidePanelItemInQueryClient(
  queryClient: QueryClient,
  newMemexItem: MemexItem | UpdateMemexItemResponseData,
) {
  const sidePanelItem = getQueryDataForSidePanelItemFromQueryClient(queryClient, newMemexItem.id.toString())
  if (sidePanelItem) {
    const mergedItem = mergeItemData(sidePanelItem, newMemexItem)
    if (mergedItem) {
      const memexItemModel = createMemexItemModel(mergedItem)
      setQueryDataForSidePanelItemInQueryClient(queryClient, memexItemModel)
    }
  }
}

/**
 * Iterates over the data included in rollbackData param and calls setQueryData for each
 * key / queryData pair
 */
export function rollbackMemexItemData(
  queryClient: QueryClient,
  rollbackData: OptimisticUpdateRollbackData | undefined,
) {
  if (!rollbackData) {
    return
  }
  for (const {queryKey, queryData} of rollbackData.queryData) {
    queryClient.setQueryData<MemexItemsPageQueryData>(queryKey, queryData)
  }
  const variables = getActiveMemexItemsQueryVariables(queryClient)
  const totalCountsQueryKey = buildPaginatedTotalCountsQueryKey(variables)
  queryClient.setQueryData<MemexItemsTotalCountsQueryData>(totalCountsQueryKey, rollbackData.totalCounts)
}

/**
 * For a the memex items provided, update their group by moving them out of their existing query,
 * and into a query for the new group. We don't try to position the items within the new group, and
 * instead just add to the top of the first page of data for the relevant group/secondary group.
 */
export function updateGroupForMemexItems(
  queryClient: QueryClient,
  itemsToMove: Array<MemexItemModel>,
  newGroupId: GroupId,
  newSecondaryGroupId?: GroupId,
) {
  const variables = getActiveMemexItemsQueryVariables(queryClient)
  const pageParamsQueryData = getPageParamsQueryDataForVariables(queryClient, variables)
  if (!isPageParamsDataGrouped(pageParamsQueryData)) {
    // We should never hit this, because if we're passing a `PageTypeForGroupedItems` then we should have grouped page params
    return
  }

  const queryKeysToItemsMap = new Map<PaginatedMemexItemsQueryKey, Set<MemexItemModel>>()
  // Build map of query key with the items to remove from that query
  for (const item of itemsToMove) {
    const queryKeysForItem = getQueryKeysForItem(queryClient, item.id)
    if (queryKeysForItem) {
      for (const queryKeyForItem of queryKeysForItem) {
        const itemsForQueryKey = queryKeysToItemsMap.get(queryKeyForItem)
        if (itemsForQueryKey) {
          itemsForQueryKey.add(item)
        } else {
          queryKeysToItemsMap.set(queryKeyForItem, new Set([item]))
        }
      }
    }
  }

  // Determine query key of query to move items to. We can't reliably predict the final order,
  // so we'll just place them somewhere and then rely on a live update to get the final positioning.
  const groupId = newGroupId
  const secondaryGroupId = newSecondaryGroupId
  const pageType = {groupId, secondaryGroupId}
  const queryKeyToAddItemsTo = getQueryKeyForFirstGroupedItemsPage(queryClient, pageType)

  const originalTotalCounts = getQueryDataForMemexItemsTotalCounts(queryClient, variables)
  const rollbackData: OptimisticUpdateRollbackData = {queryData: [], totalCounts: originalTotalCounts}
  // We'll use this to keep track of the changes to total count values for the different
  // groups as we iterate over affected query keys.
  const totalCountIncrements: {[groupId: GroupId]: number} = {}

  // Iterate over query keys and remove items from those queries
  for (const [queryKeyToRemoveFrom, itemsToRemove] of queryKeysToItemsMap.entries()) {
    queryClient.setQueryData<MemexPageQueryData>(queryKeyToRemoveFrom, queryData => {
      if (!isQueryPageOfItems(queryData)) {
        return queryData
      }

      rollbackData.queryData.push({queryData, queryKey: queryKeyToRemoveFrom})
      const oldItemModels = queryData.nodes
      // Exclude any items that match the item ids to remove for this query
      const newItemModels = oldItemModels.filter(i => !itemsToRemove.has(i))

      return {
        ...queryData,
        nodes: newItemModels,
      }
    })

    if (isQueryKeyForGroupedItems(queryKeyToRemoveFrom)) {
      // If we're removing items from a grouped items query, we'll need to decrement the total count for the group
      const groupIdToDecrement = queryKeyToRemoveFrom[3].groupId
      if (totalCountIncrements[groupIdToDecrement]) {
        totalCountIncrements[groupIdToDecrement] -= itemsToRemove.size
      } else {
        totalCountIncrements[groupIdToDecrement] = -itemsToRemove.size
      }

      const secondaryGroupIdToDecrement = queryKeyToRemoveFrom[3].secondaryGroupId
      if (secondaryGroupIdToDecrement) {
        // If the query key has a secondary group id, we need to decrement the total count for that group as well
        if (totalCountIncrements[secondaryGroupIdToDecrement]) {
          totalCountIncrements[secondaryGroupIdToDecrement] -= itemsToRemove.size
        } else {
          totalCountIncrements[secondaryGroupIdToDecrement] = -itemsToRemove.size
        }
      }
    }
  }

  // Check to see if the target query for the move is empty.
  // If there is data, we can continue as normal.
  if (queryClient.getQueryData(queryKeyToAddItemsTo)) {
    // Insert items to beginning of query
    queryClient.setQueryData<MemexPageQueryData>(queryKeyToAddItemsTo, queryData => {
      if (!isQueryPageOfItems(queryData)) {
        return queryData
      }

      rollbackData.queryData.push({queryData, queryKey: queryKeyToAddItemsTo})
      const oldItemModels = queryData.nodes
      // Insert all of the items to move
      const newItemModels = [...itemsToMove, ...oldItemModels]

      return {
        ...queryData,
        nodes: newItemModels,
      }
    })
  } else {
    // If there isn't a query for our target query key, we'll need to set up the
    // query, by updating the page params in addition to the items query data.

    queryClient.setQueryData<MemexPageQueryData>(queryKeyToAddItemsTo, {
      nodes: itemsToMove,
      pageInfo: {hasNextPage: false, hasPreviousPage: false},
    })

    const groupedItemsId = createGroupedItemsId(pageType)

    // We will also have to update the page params, so that the query gets tracked.
    const newPageParams: GroupedPageParamsQueryData = {
      ...pageParamsQueryData,
      groupedItems: {...pageParamsQueryData.groupedItems, [groupedItemsId]: [pageParamForInitialPage]},
    }
    setPageParamsQueryDataForVariables(queryClient, variables, newPageParams)

    rollbackData.queryData.push({
      queryKey: queryKeyToAddItemsTo,
      queryData: {
        nodes: [],
        pageInfo: {hasNextPage: false, hasPreviousPage: false},
      },
    })
  }

  // Now we need to track the changes to the total counts for the target group and secondary group
  if (totalCountIncrements[groupId]) {
    totalCountIncrements[groupId] += itemsToMove.length
  } else {
    totalCountIncrements[groupId] = itemsToMove.length
  }

  if (secondaryGroupId) {
    if (totalCountIncrements[secondaryGroupId]) {
      totalCountIncrements[secondaryGroupId] += itemsToMove.length
    } else {
      totalCountIncrements[secondaryGroupId] = itemsToMove.length
    }
  }

  const newTotalCounts: Omit<MemexItemsTotalCountsQueryData, 'totalCount'> = {
    groups: {},
  }

  // Finally we iterate over the total count increments we've gathered up
  // and increment the total counts for the groups that have been affected based on the
  // existing total counts.
  for (const [groupIdToIncrement, increment] of Object.entries(totalCountIncrements)) {
    newTotalCounts.groups[groupIdToIncrement] = incrementMemexItemsTotalCountForGroup(
      queryClient,
      variables,
      groupIdToIncrement,
      increment,
    )
  }
  // Then we can merge these new total counts into the existing total counts.
  mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, newTotalCounts)

  return rollbackData
}

const findTargetQueryKeyFromReorderItemData = (
  queryClient: QueryClient,
  reorderItemData: ReorderItemData,
): Array<PaginatedMemexItemsQueryKey> | undefined => {
  const queryKeys: Array<PaginatedMemexItemsQueryKey> = []
  if ('overItemId' in reorderItemData) {
    // The item for overItemId may exist in multiple queries (e.g. if it appears in multiple groups)
    const queryKeysForOverItem = getQueryKeysForItem(queryClient, reorderItemData.overItemId)
    if (!queryKeysForOverItem) return undefined
    // If group info is provided, use that to filter to the correct query key
    if (reorderItemData.overGroupId) {
      const groupedItemsPageType = {
        groupId: reorderItemData.overGroupId,
        secondaryGroupId: reorderItemData.overSecondaryGroupId,
      }
      const queryKey = queryKeysForOverItem.find(key => isQueryKeyForGroupedItemsPageType(key, groupedItemsPageType))
      if (queryKey) {
        queryKeys.push(queryKey)
      }
    }
    // If no grouping info provided, we should have at least one query key to return
    else if (queryKeysForOverItem && queryKeysForOverItem.length > 0) {
      queryKeys.push(...queryKeysForOverItem)
    }
  } else {
    // If we don't have an `overItemId` in the `reorderItemData`, then we have an `overGroupId`
    // (and possibly an `overSecondaryGroupId`) and we must be adding to an empty group,
    // so get the first (empty) page of the group
    const queryKeyForGroup = getQueryKeyForFirstGroupedItemsPage(queryClient, {
      groupId: reorderItemData.overGroupId,
      secondaryGroupId: reorderItemData.overSecondaryGroupId,
    })
    if (queryKeyForGroup) {
      queryKeys.push(queryKeyForGroup)
    }
  }
  return queryKeys.length > 0 ? queryKeys : undefined
}

/**
 * An implementation optimistically reordering a memex item that does not assume
 * that all items are in the same query page.
 * @param queryClient
 * @param movingItemId The id of the item that we're moving
 * @param reorderItemData Metadata about where we're moving the item to.
 * @returns {previousItemId: number | ''} | void: The id of the item that the moving item should be placed after
 * or void if we made no changes
 */
export function updateMemexItemPositionInQueryClient(
  queryClient: QueryClient,
  movingItemId: number,
  reorderItemData: ReorderItemData,
  sortedColumnIds?: ReadonlyArray<MemexProjectColumnId>,
): {previousItemId: number | '' | undefined; rollbackData: OptimisticUpdateRollbackData} | void {
  const variables = getActiveMemexItemsQueryVariables(queryClient)
  const originalTotalCounts = getQueryDataForMemexItemsTotalCounts(queryClient, variables)
  const rollbackData: OptimisticUpdateRollbackData = {queryData: [], totalCounts: originalTotalCounts}
  // Find the query(s) for the item that is being moved and remove it from the old query(s)
  const allQueryKeysToRemoveFrom = getQueryKeysForItem(queryClient, movingItemId)
  const allQueryKeysToAddTo = findTargetQueryKeyFromReorderItemData(queryClient, reorderItemData)

  const targetIsSubIssue = 'parentItemId' in reorderItemData && !!reorderItemData.parentItemId
  const hasSourceParent = 'sourceParentItemId' in reorderItemData && !!reorderItemData.sourceParentItemId
  const itemInSubIssueQueries = allQueryKeysToRemoveFrom?.some(isQueryKeyForSubIssues) ?? false

  // Add to sub-issue queries when targeting a sub-issue position, otherwise top-level
  const queryKeysToAddTo = allQueryKeysToAddTo?.filter(key =>
    targetIsSubIssue ? isQueryKeyForSubIssues(key) : !isQueryKeyForSubIssues(key),
  )

  let queryKeysToRemoveFrom = allQueryKeysToRemoveFrom
  if (targetIsSubIssue) {
    if (itemInSubIssueQueries) {
      // Reorder/reparent within sub-issues — only remove from sub-issue queries
      queryKeysToRemoveFrom = allQueryKeysToRemoveFrom?.filter(isQueryKeyForSubIssues)
    }
    // else: Moving from top-level into sub-issues — remove from wherever the item exists
  } else if (hasSourceParent && itemInSubIssueQueries) {
    // Un-parenting: remove from both top-level (reposition) and sub-issue queries (old parent)
    queryKeysToRemoveFrom = allQueryKeysToRemoveFrom
  } else {
    // Top-level reorder — only affect top-level queries
    queryKeysToRemoveFrom = allQueryKeysToRemoveFrom?.filter(key => !isQueryKeyForSubIssues(key))
  }

  if (!queryKeysToAddTo || !queryKeysToRemoveFrom) {
    return
  }

  const isSameSortGroup = sortedColumnIds?.length
    ? (a: MemexItemModel, b: MemexItemModel) =>
        sortedColumnIds.every(colId => {
          const aVal = a.memexProjectColumnValues.find(cv => cv.memexProjectColumnId === colId)?.value
          const bVal = b.memexProjectColumnValues.find(cv => cv.memexProjectColumnId === colId)?.value
          return isEqual(aVal, bVal)
        })
    : null

  let itemToMove: MemexItemModel | undefined = undefined
  let previousItemId: number | '' = ''

  for (const queryKeyToRemoveFrom of queryKeysToRemoveFrom) {
    queryClient.setQueryData<MemexPageQueryData>(queryKeyToRemoveFrom, queryData => {
      if (!isQueryPageOfItems(queryData)) {
        return queryData
      }

      rollbackData.queryData.push({queryKey: queryKeyToRemoveFrom, queryData})

      const oldItemModels = queryData.nodes
      const itemIndex = oldItemModels.findIndex(item => item.id === movingItemId)
      // Go ahead and save off the item that we're moving so that we don't
      // have to look it up again later
      itemToMove = oldItemModels[itemIndex]
      const newItemModels = [...oldItemModels]
      // Remove the item from its current position
      newItemModels.splice(itemIndex, 1)
      return {
        ...queryData,
        nodes: newItemModels,
      }
    })
  }

  for (const queryKeyToAddTo of queryKeysToAddTo) {
    queryClient.setQueryData<MemexPageQueryData>(queryKeyToAddTo, queryData => {
      // If we weren't able to find an item to remove previously, just return the query data
      // without any changes
      if (!itemToMove) {
        return queryData
      }

      if (!queryData && reorderItemData.overGroupId) {
        // If we don't have any query data for this query key, it means
        // we are trying to move into an empty group / cell that we
        // don't have a query for yet.

        const oldPageParams = getPageParamsQueryDataForVariables(queryClient, variables)
        const pageTypeFromQueryKey = getPageTypeFromQueryKey(queryKeyToAddTo)

        if (isPageParamsDataGrouped(oldPageParams) && isPageTypeForGroupedItems(pageTypeFromQueryKey)) {
          const groupedItemsId = createGroupedItemsId(pageTypeFromQueryKey)
          const newPageParams: GroupedPageParamsQueryData = {
            ...oldPageParams,
            groupedItems: {...oldPageParams.groupedItems, [groupedItemsId]: [pageParamForInitialPage]},
          }
          // We need to track the query that we're adding new data for.
          setPageParamsQueryDataForVariables(queryClient, variables, newPageParams)

          // If we're adding / removing from the same query, we don't want to add to the
          // rollback data twice
          if (!queryKeysToRemoveFrom.includes(queryKeyToAddTo)) {
            rollbackData.queryData.push({
              queryKey: queryKeyToAddTo,
              // The rollback data should be an empty list of items.
              queryData: {nodes: [], pageInfo: {hasNextPage: false, hasPreviousPage: false}},
            })
          }

          return {
            nodes: [itemToMove],
            pageInfo: {hasNextPage: false, hasPreviousPage: false},
          }
        }
      }

      if (!isQueryPageOfItems(queryData)) {
        return queryData
      }

      // If we're adding / removing from the same query, we don't want to add to the
      // rollback data twice
      if (!queryKeysToRemoveFrom.includes(queryKeyToAddTo)) {
        rollbackData.queryData.push({queryKey: queryKeyToAddTo, queryData})
      }

      if (!('overItemId' in reorderItemData)) {
        // we don't know anything about where to add
        // the item, so just add it to the end.
        // Currently this should only be used for an empty group
        return {
          ...queryData,
          nodes: [...queryData.nodes, itemToMove],
        }
      }

      const {overItemId, side} = reorderItemData

      const oldItemModels = queryData.nodes
      const itemIndex = oldItemModels.findIndex(item => item.id === overItemId)
      const newItemIndex = side === 'before' ? itemIndex : itemIndex + 1
      if (newItemIndex > 0) {
        const previousItem = oldItemModels[newItemIndex - 1]
        if (previousItem) {
          previousItemId = previousItem.id

          // In sorted view the cache predecessor may be from a different sort group.
          // Use '' (top of priority) when that happens so the server places the item correctly.
          if (isSameSortGroup && !isSameSortGroup(itemToMove, previousItem)) {
            previousItemId = ''
          }
        }
      } else {
        previousItemId = ''
      }
      const newItemModels = [...oldItemModels]

      // Add the item that has already been removed to its new position
      newItemModels.splice(newItemIndex, 0, itemToMove)
      return {
        ...queryData,
        nodes: newItemModels,
      }
    })
  }

  for (const queryKeyToRemoveFrom of queryKeysToRemoveFrom) {
    const removeFromPageType = getPageTypeFromQueryKey(queryKeyToRemoveFrom)
    for (const queryKeyToAddTo of queryKeysToAddTo) {
      const addToPageType = queryKeyToAddTo[3] // page type is always at index 3

      if (isPageTypeForGroupedItems(removeFromPageType) && isPageTypeForGroupedItems(addToPageType)) {
        const newTotalCounts: Omit<MemexItemsTotalCountsQueryData, 'totalCount'> = {
          groups: {},
        }

        if (removeFromPageType.groupId !== addToPageType.groupId) {
          const removeFromGroupId = removeFromPageType.groupId
          const addToGroupId = addToPageType.groupId
          newTotalCounts.groups[removeFromGroupId] = incrementMemexItemsTotalCountForGroup(
            queryClient,
            variables,
            removeFromGroupId,
            -1,
          )
          newTotalCounts.groups[addToGroupId] = incrementMemexItemsTotalCountForGroup(
            queryClient,
            variables,
            addToGroupId,
            1,
          )
        }

        if (
          removeFromPageType.secondaryGroupId !== addToPageType.secondaryGroupId &&
          removeFromPageType.secondaryGroupId != null &&
          addToPageType.secondaryGroupId != null
        ) {
          const removeFromGroupId = removeFromPageType.secondaryGroupId
          const addToGroupId = addToPageType.secondaryGroupId
          newTotalCounts.groups[removeFromGroupId] = incrementMemexItemsTotalCountForGroup(
            queryClient,
            variables,
            removeFromGroupId,
            -1,
          )
          newTotalCounts.groups[addToGroupId] = incrementMemexItemsTotalCountForGroup(
            queryClient,
            variables,
            addToGroupId,
            1,
          )
        }

        mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, newTotalCounts)
      }
    }
  }

  if ('overItemId' in reorderItemData) {
    return {previousItemId, rollbackData}
  } else {
    // We don't want to return anything  for the previousItemId if we're adding to an empty group
    // as it should not represent a change in priority, just a change in the group value
    return {previousItemId: undefined, rollbackData}
  }
}

/**
 * Updates the local state of a MemexItemModel with new values from the server. If the item cannot be found, nothing happens;
 * otherwise, a new MemexItemModel is created to replace the existing item in our client-side state and a new array identity
 * is created, so that a re-render is triggered.
 * If the item exists in multiple queries (e.g., it appears in multiple groups), all instances will be updated.
 * @param queryClient
 * @param newMemexItem New data that we're looking to merge values into an existingMemexItemModel in our client state
 */
export function updateMemexItemInQueryClient(
  queryClient: QueryClient,
  newMemexItem: MemexItem | UpdateMemexItemResponseData,
  skipSidePanel: boolean = false,
): void {
  if (!skipSidePanel) {
    updateSidePanelItemInQueryClient(queryClient, newMemexItem)
  }

  // The general algorithm here is as follows:
  // Find the query key of the query that contains the item we're looking to update.
  // Then call `setQueryData` with that query key, and find the index of the item in the page/query.
  // Next, we merge the item data, and update the item in the page/query, and return the new array of nodes.
  const queryKeysForQueryContainingItem = getQueryKeysForItem(queryClient, newMemexItem.id)
  if (!queryKeysForQueryContainingItem) {
    // Return early if the item is not in the main query cache
    return
  }
  for (const queryKeyForQueryContainingItem of queryKeysForQueryContainingItem) {
    queryClient.setQueryData<MemexItemsPageQueryData>(queryKeyForQueryContainingItem, queryData => {
      if (!queryData) {
        return undefined
      }
      const newPageNodes = [...queryData.nodes]
      const indexOnPage = newPageNodes.findIndex(item => item.id === newMemexItem.id)
      const itemModel = newPageNodes[indexOnPage]
      if (itemModel) {
        const mergedItem = mergeItemData(itemModel, newMemexItem)
        if (mergedItem) {
          const newItemModel = createMemexItemModel(mergedItem)
          newPageNodes[indexOnPage] = newItemModel
        }
      }
      return {...queryData, nodes: newPageNodes}
    })
  }
}

/**
 * Applies a transform to the `agentTasks` of a single item in the main query cache,
 * without touching any other item fields. Used by client-side agent task hydration
 * (`memex_lazy_hydrate_agent_tasks`) to merge freshly fetched task state into the
 * cached item while preserving every other (possibly optimistically updated) field.
 *
 * The item is reconstructed from its existing model via `mergeItemData(model, model)`
 * (which preserves columns and all other fields), then its `agentTasks` are replaced
 * with the result of `updateTasks`. A new model and array identity are produced so a
 * re-render is triggered. If the tasks are unchanged, the cache is left untouched to
 * avoid needless renders.
 *
 * @param queryClient
 * @param itemId The memex item id whose agent tasks should be updated
 * @param updateTasks Receives the item's current agent tasks and returns the next array
 */
export function updateAgentTasksForItemInQueryClient(
  queryClient: QueryClient,
  itemId: number,
  updateTasks: (currentTasks: Array<AgentTask>) => Array<AgentTask>,
): void {
  const queryKeysForQueryContainingItem = getQueryKeysForItem(queryClient, itemId)
  if (!queryKeysForQueryContainingItem) {
    return
  }
  for (const queryKeyForQueryContainingItem of queryKeysForQueryContainingItem) {
    queryClient.setQueryData<MemexItemsPageQueryData>(queryKeyForQueryContainingItem, queryData => {
      if (!queryData) {
        return undefined
      }
      const indexOnPage = queryData.nodes.findIndex(item => item.id === itemId)
      const itemModel = queryData.nodes[indexOnPage]
      if (!itemModel) {
        return queryData
      }

      const currentTasks = itemModel.agentTasks ?? []
      const nextTasks = updateTasks(currentTasks)
      if (isEqual(currentTasks, nextTasks)) {
        return queryData
      }

      const mergedItem = mergeItemData(itemModel, itemModel)
      if (!mergedItem) {
        return queryData
      }
      mergedItem.agentTasks = nextTasks

      const newPageNodes = [...queryData.nodes]
      newPageNodes[indexOnPage] = createMemexItemModel(mergedItem)
      return {...queryData, nodes: newPageNodes}
    })
  }
}

/**
 * A wrapper around setQueryData for setting query data for a single memex item model
 * that we want to show in the side panel.
 * If the item model is already in the main client-side state, updates that
 * copy of the item too.
 */
export function setQueryDataForSidePanelItemInQueryClient(queryClient: QueryClient, queryData: MemexItemModel) {
  const itemInMainCache = findMemexItemByIdInQueryClient(queryClient, queryData.id)
  const sidePanelQueryKey = useSidePanelItemQuery.getKey({itemId: queryData.id.toString()})
  if (itemInMainCache) {
    // If the item exists in the main query cache, update it
    updateMemexItemInQueryClient(queryClient, queryData, true)
  }
  // Always set item in the side panel cache.
  queryClient.setQueryData<MemexItemModel>(sidePanelQueryKey, queryData)
}
